import React from 'react';
import styled from 'styled-components';

import { rhythm } from '../utils/typography';

type Cell = {
  colspan?: number;
  rowspan?: number;
  value: string;
};

type DataTable = {
  thead: (Cell | string)[][];
  tbody: (Cell | string)[][];
};

type Props = {
  children: string;
  size?: 'sm' | '';
  maxHeight?: number;
};

const Container = styled.div`
  display: block;
  width: 100%;
  margin-bottom: ${rhythm(1)};
  padding-bottom: ${rhythm(0.5)};
  overflow-x: auto;
  white-space: nowrap;

  ${({ maxHeight }: { maxHeight?: number }) => {
    return `
      max-height: ${maxHeight}px;
      overflow-y: auto;
    `;
  }}
`;

const Table = styled.table`
  margin-bottom: 0;

  ${({ size }: { size?: 'sm' | '' }) => {
    if (size === 'sm') {
      return `
        font-size: ${rhythm(0.5)};
        line-height: ${rhythm(0.5)};

        th,
        td {
          padding: ${rhythm(0.2)} ${rhythm(0.3)};
          white-space: nowrap;
        }
      `;
    }
  }}
`;

const ResponsiveTable: React.FC<Props> = ({ size, children, maxHeight }) => {
  const { thead, tbody }: DataTable = JSON.parse(children);

  const renderCol = (
    col: Cell | string,
    key: number,
    Tag: 'th' | 'td'
  ): React.ReactNode => {
    let rowspan = 1;
    let colspan = 1;
    let value = '';

    if (typeof col !== 'string') {
      rowspan = col.rowspan || rowspan;
      colspan = col.colspan || colspan;
      value = col.value;
    } else {
      value = col;
    }

    if (value[0] === '`' && value[value.length - 1]) {
      value = `<code>${value.substring(1, value.length - 1)}</code>`;
    }

    if (value.indexOf('^') > -1) {
      const split = value.split('^');
      for (let i = 0; i < split.length; ++i) {
        if (i % 1 === 0 && i > 0) {
          split[i] = `<sup>${split[i][0]}</sup>${split[i].substring(
            1,
            split[i].length
          )}`;
        }
      }

      value = split.join('');
    }

    return (
      <Tag
        key={key}
        rowSpan={rowspan}
        colSpan={colspan}
        dangerouslySetInnerHTML={{ __html: value }}
      ></Tag>
    );
  };

  return (
    <Container maxHeight={maxHeight}>
      <Table size={size}>
        {thead && (
          <thead>
            {thead.map((row, i) => {
              return (
                <tr key={i}>{row.map((col, j) => renderCol(col, j, 'th'))}</tr>
              );
            })}
          </thead>
        )}
        {tbody && (
          <tbody>
            {tbody.map((row, i) => {
              return (
                <tr key={i}>{row.map((col, j) => renderCol(col, j, 'td'))}</tr>
              );
            })}
          </tbody>
        )}
      </Table>
    </Container>
  );
};

export default ResponsiveTable;
